import axios from "axios";

import { clearSession, getRefreshToken, getSessionExpiresAt, getSessionToken, saveSession } from "./session";

export const AUTH_LOGOUT_EVENT = "lumen:auth-logout";
const REFRESH_LEAD_MS = 60 * 1000;
const MIN_REFRESH_DELAY_MS = 30 * 1000;

const authClient = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
let refreshInFlight = null;
let refreshTimer = null;

const isBrowserOnline = () => (typeof navigator === "undefined" ? true : navigator.onLine !== false);

export const isExpiredTokenError = (error) => {
  if (error?.response?.status !== 401) {
    return false;
  }
  const detail = String(error.response?.data?.detail || "").toLowerCase();
  const reason = String(error.response?.headers?.["x-auth-reason"] || "").toLowerCase();
  return reason === "token_expired" || reason === "invalid_token" || detail.includes("expired") || detail.includes("invalid jwt") || detail.includes("bad_jwt");
};

export const forceLogout = (reason) => {
  clearSession();
  stopProactiveRefresh();
  window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT, { detail: { reason } }));
};

export const refreshSession = () => {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new Error("No refresh token available."));
  }
  refreshInFlight = authClient
    .post("/auth/refresh", { refresh_token: refreshToken })
    .then(({ data }) => {
      if (!data?.access_token) {
        throw new Error("Incomplete refresh response.");
      }
      saveSession(data);
      scheduleProactiveRefresh();
      return data;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
};

export const stopProactiveRefresh = () => {
  if (refreshTimer) {
    window.clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

export const scheduleProactiveRefresh = () => {
  stopProactiveRefresh();
  const expiresAt = getSessionExpiresAt();
  if (!expiresAt || !getRefreshToken()) {
    return;
  }
  const delay = Math.max(MIN_REFRESH_DELAY_MS, expiresAt - Date.now() - REFRESH_LEAD_MS);
  refreshTimer = window.setTimeout(() => {
    if (!isBrowserOnline()) {
      refreshTimer = null;
      scheduleRetrySoon();
      return;
    }
    refreshSession().catch((error) => {
      const status = error?.response?.status;
      if (status && status >= 400 && status < 500) {
        forceLogout("expired");
        return;
      }
      scheduleRetrySoon();
    });
  }, delay);
};

const scheduleRetrySoon = () => {
  stopProactiveRefresh();
  refreshTimer = window.setTimeout(() => scheduleProactiveRefresh(), MIN_REFRESH_DELAY_MS);
};

export const attachAuthRefresh = (client) => {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error?.config;
      if (!original || original._authRetried || !isExpiredTokenError(error) || !getRefreshToken()) {
        return Promise.reject(error);
      }
      original._authRetried = true;
      try {
        const session = await refreshSession();
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${session.access_token}` };
        return client(original);
      } catch (refreshError) {
        const status = refreshError?.response?.status;
        if ((status && status >= 400 && status < 500) || (!refreshError?.response && !refreshError?.request)) {
          forceLogout("expired");
        }
        return Promise.reject(error);
      }
    },
  );
};

export const hasValidSession = () => Boolean(getSessionToken());
